import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Globe from 'react-globe.gl';
import HeroCarousel from '../../components/HeroCarousel';
import './AboutUs.css';

const AboutUs = () => {
  const globeEl = useRef();

  useEffect(() => {

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    }, observerOptions);

    const fadeElements = document.querySelectorAll('.fade-in-section');
    fadeElements.forEach(el => observer.observe(el));

    // Configure globe
    if (globeEl.current) {
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.5;
      globeEl.current.controls().enableZoom = false;
      globeEl.current.pointOfView({ altitude: 2.5 });
    }

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const networkDefining = [
    {
      id: 1,
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: 'Global Representation',
      description: 'Every member operates as an equal partner within a trusted global network, ensuring fair collaboration, mutual respect, and balanced business opportunities across all regions.'
    },
    {
      id: 2,
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: 'Member-Focused Growth',
      description: 'Our programs and initiatives are designed to support member expansion, strengthen international partnerships, and drive long-term business growth.'
    },
    {
      id: 3,
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
      ),
      title: 'Trusted Governance',
      description: 'We maintain clear, structured, and accountable processes that build trust, ensure consistency, and support seamless global operations.'
    },
    {
      id: 4,
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      ),
      title: 'Business-Focused Value',
      description: 'Focused on delivering measurable outcomes through strong partnerships, increased collaboration, and sustainable growth opportunities.'
    }
  ];

  const stats = [
    { 
      number: '500+',
      label: 'Global Members',
      verified: true,
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      )
    },
    { 
      number: '150+',
      label: 'Countries',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      )
    },
    { 
      number: '5', 
      label: 'Continents',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      )
    },
    { 
      number: '24/7', 
      label: 'Support',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      )
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Stats Bar */}
      <section className="stats-bar-section">
        <div className="container">
          <div className="stats-bar-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-bar-item">
                <div className="stat-bar-icon">{stat.icon}</div>
                {stat.verified ? (
                  <div className="stat-bar-number stat-verified-number">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="rgba(16,185,129,0.12)" stroke="#10b981" strokeWidth="1.5"/>
                      <path d="M9 12l2 2 4-4" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ) : (
                  <div className="stat-bar-number">{stat.number}</div>
                )}
                <div className="stat-bar-label">{stat.verified ? 'Verified Global Members' : stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Banner */}

      {/* Our Purpose Section */}
      <section className="purpose-section fade-in-section">
        <div className="container">
          <div className="purpose-layout">
            <div className="purpose-content-left">
              <div className="purpose-eyebrow">Our Mission</div>
              <h2 className="section-title-left">Our Purpose</h2>
              <p className="section-description-large">
               EFFN’s mission is to empower independent freight forwarders by providing access to global partnerships, industry insights, and valuable business opportunities that drive growth and operational excellence.
              </p>
              <p className="section-description-large">
                We achieve this by building trust within our network, facilitating meaningful connections, sharing industry knowledge, supporting member growth, and providing financial security through our reliable protection program.
              </p>
            </div>
            <div className="purpose-visual">
              <div className="purpose-card">
                <div className="purpose-card-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                </div>
                <h3 className="purpose-card-title">Global Network</h3>
                <p className="purpose-card-text">
                  Connect with trusted partners across every major trade region worldwide.
                </p>
              </div>
              <div className="purpose-card">
                <div className="purpose-card-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <h3 className="purpose-card-title">Strategic Growth</h3>
                <p className="purpose-card-text">
                  Access resources and partnerships designed to accelerate your business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="who-we-are-section fade-in-section">
        <div className="container">
          <div className="who-layout">
            <div className="who-content-side">
              <div className="who-eyebrow">About Us</div>
              <h2 className="section-title-left">What is EFFN</h2>
              <p className="section-description-large">
               EFFN is a global professional association of independent freight forwarders and logistics service providers. We provide a collaborative platform where member companies retain their unique identity and operational independence while gaining access to a coordinated network of trusted partners worldwide.
              </p>
              <p className="section-description-large">
                Unlike traditional freight forwarding consolidators, EFFN preserves member autonomy while offering the infrastructure, tools, and connections needed to compete successfully in international markets. Members maintain full control over their operations, pricing, and client relationships, all while benefiting from the strength, resources, and opportunities of a global logistics network.
              </p>
              <div className="who-highlight-box">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <p>
                  EFFN is built on the principle that independent logistics operators can compete effectively on a global stage without sacrificing their operational independence or brand identity. By connecting capable, professional logistics operators across borders and continents, we strengthen the entire supply chain industry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Defines Our Network Section */}
      <section className="network-defining-section fade-in-section">
        <div className="container">
          <div className="section-header-center">
            <div className="defining-eyebrow">Our Values</div>
            <h2 className="section-title">What Defines Our Network</h2>
            <p className="section-subtitle">
              A network built on trust and collaboration, driving growth and success for every member.
            </p>
          </div>
          <div className="defining-grid">
            {networkDefining.map((item) => (
              <div key={item.id} className="defining-card">
                <div className="defining-number">0{item.id}</div>
                <div className="defining-icon">
                  {item.icon}
                </div>
                <h3 className="defining-title">{item.title}</h3>
                <p className="defining-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="global-presence-section fade-in-section">
        <div className="container">
          <div className="global-layout">
            <div className="global-content">
              <div className="global-eyebrow">Worldwide Reach</div>
              <h2 className="section-title-left">A Truly Global Logistics Network</h2>
              <p className="section-description-large">
                With strong representation across key international trade regions, our network enables seamless cross-border collaboration and reliable global coverage.
              </p>
              <p className="section-description-large">
                Members benefit from a connected ecosystem built to support international operations with confidence and consistency. Acting as a facilitator, the network empowers independent logistics professionals to collaborate efficiently and grow together.
              </p>
            </div>
            <div className="global-visual">
              <div className="global-map-container">
                <div className="world-map-wrapper">
                  <svg className="realistic-world-map" viewBox="0 0 2000 857" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      <filter id="mapGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Light Background */}
                    <rect width="2000" height="857" fill="#f0f4f8"/>
                    
                    {/* World Map - All Countries */}
                    <image 
                      href="https://upload.wikimedia.org/wikipedia/commons/8/83/Equirectangular_projection_SW.jpg" 
                      width="2000" 
                      height="857" 
                      opacity="0.9"
                      preserveAspectRatio="xMidYMid meet"
                    />
                    
                    {/* Highlighted Countries Overlay (Golden) */}
                    <g className="highlighted-countries" fill="#D4AF37" fillOpacity="0.4" stroke="#D4AF37" strokeWidth="1.5">
                      {/* USA */}
                      <circle cx="350" cy="310" r="45" opacity="0.3"/>
                      {/* Brazil */}
                      <circle cx="430" cy="610" r="40" opacity="0.3"/>
                      {/* UK */}
                      <circle cx="950" cy="280" r="35" opacity="0.3"/>
                      {/* Nigeria */}
                      <circle cx="1010" cy="520" r="35" opacity="0.3"/>
                      {/* Singapore */}
                      <circle cx="1360" cy="470" r="30" opacity="0.3"/>
                      {/* Australia */}
                      <circle cx="1580" cy="670" r="45" opacity="0.3"/>
                    </g>
                    
                    {/* Connection Arcs */}
                    <g className="connection-arcs" stroke="#D4AF37" strokeWidth="2.5" fill="none" opacity="0.7" filter="url(#mapGlow)">
                      {/* USA to UK */}
                      <path d="M 350 310 Q 650 200, 950 280" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="4s" repeatCount="indefinite"/>
                      </path>
                      {/* USA to Brazil */}
                      <path d="M 350 310 Q 390 460, 430 610" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="3.5s" repeatCount="indefinite"/>
                      </path>
                      {/* UK to Nigeria */}
                      <path d="M 950 280 Q 980 400, 1010 520" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="3s" repeatCount="indefinite"/>
                      </path>
                      {/* UK to Singapore */}
                      <path d="M 950 280 Q 1155 375, 1360 470" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="4.5s" repeatCount="indefinite"/>
                      </path>
                      {/* Nigeria to Singapore */}
                      <path d="M 1010 520 Q 1185 495, 1360 470" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="4s" repeatCount="indefinite"/>
                      </path>
                      {/* Brazil to Nigeria */}
                      <path d="M 430 610 Q 720 565, 1010 520" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="5s" repeatCount="indefinite"/>
                      </path>
                      {/* Singapore to Australia */}
                      <path d="M 1360 470 Q 1470 570, 1580 670" strokeDasharray="10,8">
                        <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="3.5s" repeatCount="indefinite"/>
                      </path>
                    </g>
                    
                    {/* City Markers with Labels */}
                    <g className="city-markers-labeled">
                      {/* New York */}
                      <g transform="translate(350, 310)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">New York</text>
                      </g>
                      
                      {/* Sao Paulo */}
                      <g transform="translate(430, 610)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" begin="0.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" begin="0.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">São Paulo</text>
                      </g>
                      
                      {/* London */}
                      <g transform="translate(950, 280)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" begin="1s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" begin="1s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">London</text>
                      </g>
                      
                      {/* Lagos */}
                      <g transform="translate(1010, 520)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" begin="1.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" begin="1.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">Lagos</text>
                      </g>
                      
                      {/* Singapore */}
                      <g transform="translate(1360, 470)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" begin="2s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" begin="2s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">Singapore</text>
                      </g>
                      
                      {/* Sydney */}
                      <g transform="translate(1580, 670)">
                        <circle r="0" fill="#D4AF37" opacity="0.15">
                          <animate attributeName="r" values="0;50;0" dur="3s" begin="2.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="16" fill="#D4AF37" filter="url(#mapGlow)">
                          <animate attributeName="r" values="16;19;16" dur="2s" begin="2.5s" repeatCount="indefinite"/>
                        </circle>
                        <circle r="7" fill="#FFF" opacity="0.95"/>
                        <text x="0" y="35" textAnchor="middle" fill="#D4AF37" fontSize="14" fontWeight="600">Sydney</text>
                      </g>
                    </g>
                  </svg>
                </div>
                
                {/* Connected Countries List */}
                <div className="connected-countries-list">
                  <div className="country-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>North America</span>
                  </div>
                  <div className="country-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>South America</span>
                  </div>
                  <div className="country-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>Europe</span>
                  </div>
                  <div className="country-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>Asia Pacific</span>
                  </div>
                  <div className="country-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>Africa</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join CTA Section */}
      <section className="join-cta-section fade-in-section">
        <div className="container">
          <div className="cta-card">
            <h2 className="cta-title">Grow with a Network Built for Long-Term Success</h2>
            <p className="cta-description">
              Joining our network means becoming part of a global community committed to credibility, collaboration, and sustainable business growth.
            </p>
            <p className="cta-description">
              We focus on building strong, trusted partnerships—prioritizing quality, transparency, and long-term value over volume.
            </p>
            <div className="cta-buttons">
              <Link to="/join-us" className="btn-cta-primary">
                Apply for Membership
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/contact" className="btn-cta-secondary">
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;